import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, Table, Tag, Tooltip, Typography, message,
} from 'antd';
import { SyncOutlined } from '@ant-design/icons';

import {
  getCompanyWhatsAppMergeFields,
  getCompanyWhatsAppTemplates,
  syncCompanyWhatsAppTemplates,
} from '../api';
import {
  apiError, formatDateTime, plural, unwrap,
} from './shared';
import {
  MAPPING_STATE, MergeFields, SenderTemplate, TEMPLATE_STATUS, countOf, isUsable, kindLabel,
  normalizeTemplateList, unusableReason,
} from './templateMapping';
import TemplateMappingDrawer from './TemplateMappingDrawer';

const { Text } = Typography;

interface Props {
  companyId: string;
  botmapId: string;
  /** Called after a sync or a saved mapping, so the card can refresh its history. */
  onChanged?: () => void;
}

/** Templates section of the /companies WhatsApp card (ADR-037 phase 0A, GoHire admins only). */
const SenderTemplates: React.FC<Props> = ({ companyId, botmapId, onChanged }) => {
  const [templates, setTemplates] = useState<SenderTemplate[]>([]);
  const [lastSynced, setLastSynced] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [mergeFields, setMergeFields] = useState<MergeFields | null>(null);
  const [editing, setEditing] = useState<SenderTemplate | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = normalizeTemplateList(unwrap(await getCompanyWhatsAppTemplates(companyId, botmapId)));
      setTemplates(list.templates);
      setLastSynced(list.lastSyncedAt);
    } catch (err) {
      setTemplates([]);
      setLastSynced(null);
      setError(apiError(err, 'Failed to load the templates for this number.'));
    } finally {
      setLoading(false);
    }
  }, [companyId, botmapId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let cancelled = false;
    getCompanyWhatsAppMergeFields(companyId)
      .then((res: any) => { if (!cancelled) setMergeFields(unwrap(res) || null); })
      .catch(() => { if (!cancelled) setMergeFields(null); });
    return () => { cancelled = true; };
  }, [companyId]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = unwrap(await syncCompanyWhatsAppTemplates(companyId, botmapId)) || {};
      if (Array.isArray(res.templates)) {
        const list = normalizeTemplateList(res);
        setTemplates(list.templates);
        setLastSynced(list.lastSyncedAt);
      } else {
        await load();
      }
      const total = countOf(res.synced !== undefined ? res.synced : res.total);
      const parts = [
        `${countOf(res.added)} added`,
        `${countOf(res.updated)} updated`,
        `${countOf(res.removed)} removed`,
      ];
      message.success(`Synced ${plural(total, 'template')} from Infobip · ${parts.join(', ')}`);
      if (onChanged) onChanged();
    } catch (err) {
      setError(apiError(err, 'Failed to sync templates from Infobip.'));
    } finally {
      setSyncing(false);
    }
  };

  const handleSaved = (saved: SenderTemplate) => {
    setEditing(null);
    message.success(`Mapping saved for ${saved.name}`);
    load();
    if (onChanged) onChanged();
  };

  const visible = templates.filter((t) => !t.removedAt);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, t: SenderTemplate) => {
        const reason = isUsable(t) ? null : unusableReason(t);
        return (
          <div>
            <Text style={{ color: reason ? '#8c8c8c' : undefined }}>{name}</Text>
            {reason && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block' }} data-testid="whatsapp-template-reason">
                {reason}
              </Text>
            )}
          </div>
        );
      },
    },
    { title: 'Language', dataIndex: 'language', key: 'language', width: 90 },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      render: (c: string) => (c ? c.charAt(0) + c.slice(1).toLowerCase() : ''),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s: string) => {
        const meta = TEMPLATE_STATUS[s] || { label: s, color: 'default' };
        return <Tag color={meta.color} data-testid="whatsapp-template-status">{meta.label}</Tag>;
      },
    },
    {
      title: 'Automatic use',
      dataIndex: 'kind',
      key: 'kind',
      width: 170,
      render: (kind: string | null, t: SenderTemplate) => (
        <Text type={kind ? undefined : 'secondary'} data-testid="whatsapp-template-kind">
          {isUsable(t) ? kindLabel(kind) : '—'}
        </Text>
      ),
    },
    {
      title: 'Mapping',
      dataIndex: 'mappingState',
      key: 'mappingState',
      width: 130,
      render: (state: string) => {
        const meta = MAPPING_STATE[state] || { label: state, color: 'default' };
        return <Tag color={meta.color} data-testid="whatsapp-template-mapping">{meta.label}</Tag>;
      },
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, t: SenderTemplate) => (isUsable(t) ? (
        <Button size="small" onClick={() => setEditing(t)} data-testid={`whatsapp-template-map-${t.id}`}>
          Map fields
        </Button>
      ) : null),
    },
  ];

  return (
    <div data-testid="company-whatsapp-templates">
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap',
      }}
      >
        <div>
          <Text strong style={{ display: 'block' }}>Templates</Text>
          <Text type="secondary" style={{ fontSize: 13 }} data-testid="company-whatsapp-templates-synced">
            {lastSynced ? `Last synced ${formatDateTime(lastSynced)}` : 'Not synced yet'}
          </Text>
        </div>
        <Tooltip title="Fetch the number's templates, their status and wording from Infobip. Existing mappings are kept.">
          <Button
            icon={<SyncOutlined spin={syncing} />}
            loading={false}
            disabled={syncing}
            onClick={handleSync}
            data-testid="company-whatsapp-templates-sync"
          >
            Sync from Infobip
          </Button>
        </Tooltip>
      </div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 8 }} />}
      <Table
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={visible}
        loading={loading}
        pagination={false}
        scroll={{ x: 760 }}
        onRow={(t: SenderTemplate) => ({
          'data-testid': 'whatsapp-template-row',
          'data-name': t.name,
          style: isUsable(t) ? undefined : { background: '#fafafa' },
        } as any)}
        locale={{ emptyText: 'No templates yet. Sync from Infobip to fetch the approved templates for this number.' }}
      />
      <TemplateMappingDrawer
        open={!!editing}
        companyId={companyId}
        template={editing}
        mergeFields={mergeFields}
        onClose={() => setEditing(null)}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default SenderTemplates;
